import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { organizationSchema, websiteSchema, localBusinessSchema } from '../components/SEOHead';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';
import { BackgroundVideo } from '../components/player/BackgroundVideo';
import InquiryPopup from '../components/InquiryPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_EASE = [0.22, 1, 0.36, 1];
const heroItem = (reduce, delay) => ({
  initial: reduce ? false : { opacity: 0, y: 26, filter: 'blur(6px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  transition: { duration: 1.1, delay, ease: HERO_EASE },
});


const isValidUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') ||
         url.includes('amazonaws.com') ||
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const LandingPage = () => {
  const reduce = useReducedMotion();
  const [hero, setHero] = useState(null);
  const [settings, setSettings] = useState({});
  const [featuredFilm, setFeaturedFilm] = useState(null);
  const [featuredCollection, setFeaturedCollection] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [logoUrl, setLogoUrl] = useState('');
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [showFloatHint, setShowFloatHint] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      try {
        const [filmLandingRes, settingsRes, filmsRes, collectionsRes, testimonialsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/film-landing`),
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/content/film-videos`).catch(() => null),
          fetch(`${API_URL}/api/content/collections`).catch(() => null),
          fetch(`${API_URL}/api/content/testimonials`).catch(() => null),
        ]);

        const filmLandingData = await filmLandingRes.json().catch(() => ({}));
        const settingsData = await settingsRes.json().catch(() => ({}));
        const filmsData = filmsRes ? await filmsRes.json().catch(() => []) : [];
        const collectionsData = collectionsRes ? await collectionsRes.json().catch(() => []) : [];
        const testimonialsData = testimonialsRes ? await testimonialsRes.json().catch(() => []) : [];

        if (!mounted) return;

        // Reuse Film Landing hero (video_url + fallback image) on the home hero
        setHero({
          video_url: filmLandingData?.hero_video_url || '',
          hls_url: filmLandingData?.hero_hls_url || '',
          fallback_image_url: filmLandingData?.hero_fallback_image_url || ''
        });
        setSettings(settingsData || {});

        const validLogo = settingsData.logo_url || settingsData.photo_logo_url || '';
        setLogoUrl(isValidUrl(validLogo) ? validLogo : '');

        const sortedFilms = (filmsData || []).slice().sort((a, b) => {
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          return (b.priority || 0) - (a.priority || 0);
        });
        setFeaturedFilm(sortedFilms[0] || null);

        const sortedCollections = (collectionsData || [])
          .filter(c => isValidUrl(c.cover_image_url))
          .sort((a, b) => (b.priority || 0) - (a.priority || 0));
        setFeaturedCollection(sortedCollections[0] || null);

        setTestimonials((testimonialsData || []).slice(0, 3));
      } catch (e) {
        console.error('Landing page fetch error:', e);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  // Reveal the floating Inquire button after a brief delay (less aggressive)
  useEffect(() => {
    const t = setTimeout(() => setShowFloatHint(true), 1600);
    return () => clearTimeout(t);
  }, []);

  const heroVideo = hero?.video_url && isValidUrl(hero.video_url) ? hero.video_url : '';
  const heroPoster = hero?.fallback_image_url && isValidUrl(hero.fallback_image_url) ? hero.fallback_image_url
                  : (featuredCollection?.cover_image_url || '');

  return (
    <div className="bg-white min-h-screen text-black" data-testid="landing-page">
      <SEOHead
        title="Crystal Media — Luxury Wedding Photography & Cinematic Wedding Films"
        description="Luxury wedding photographer and wedding videographer creating cinematic wedding films and timeless photography for Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings in Toronto, Vancouver, Montreal, Houston, Miami, Texas, and across Canada and the USA."
        url="/"
        jsonLd={[organizationSchema, websiteSchema, localBusinessSchema]}
        siteSettings={settings}
      />

      {/* Local crystal/glass styling — landing page only */}
      <style>{`
        .crystal-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border: 1px solid rgba(255, 255, 255, 0.22);
          color: #fff;
          font-size: 0.75rem;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          font-weight: 500;
          transition: background 300ms ease, border-color 300ms ease, color 300ms ease;
        }
        .crystal-btn:hover {
          background: rgba(255, 255, 255, 0.92);
          color: #000;
          border-color: rgba(255, 255, 255, 0.85);
        }
        .crystal-btn-solid {
          background: #fff;
          color: #000;
          border: 1px solid #fff;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
        }
        .crystal-btn-solid:hover {
          background: rgba(255, 255, 255, 0.88);
          color: #000;
        }
        .crystal-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1.75rem;
          padding: 0.5rem 0.25rem;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.7rem;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          transition: color 250ms ease, border-color 250ms ease;
        }
        .crystal-link:hover {
          color: #fff;
          border-color: #fff;
        }
        .lux-btn-dark {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: #000;
          color: #fff;
          font-size: 0.75rem;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          font-weight: 500;
          border: 1px solid #000;
          transition: background 300ms ease, transform 300ms ease, color 300ms ease;
        }
        .lux-btn-dark:hover { background: #1a1a1a; transform: translateY(-1px); }
        .lux-btn-ghost {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: transparent;
          color: rgba(0, 0, 0, 0.7);
          font-size: 0.75rem;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          font-weight: 500;
          border: 1px solid rgba(0, 0, 0, 0.2);
          transition: background 300ms ease, color 300ms ease, border-color 300ms ease;
        }
        .lux-btn-ghost:hover { background: #000; color: #fff; border-color: #000; }
        @keyframes floatIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .float-in { animation: floatIn 600ms cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>

      <Header logoUrl={logoUrl} variant="transparent" />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[88vh] md:min-h-screen flex items-center justify-center overflow-hidden" data-testid="landing-hero">
        {heroPoster && (
          <img
            src={heroPoster}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        )}
        {heroVideo && (
          <div className={`absolute inset-0 transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`} data-testid="landing-hero-video">
            <BackgroundVideo
              hlsUrl={hero?.hls_url || ''}
              mp4Url={heroVideo}
              poster={heroPoster}
              onReady={() => setHeroLoaded(true)}
            />
          </div>
        )}

        {/* Cinematic dark overlay (no gold) */}
        <div className="absolute inset-0 bg-black/55" />
        {/* Subtle vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 90%)' }}
        />

        {/* Content */}
        <div className="relative z-10 text-center px-6 py-20 max-w-3xl mx-auto">
          <motion.p
            {...heroItem(reduce, 0.15)}
            className="text-[10px] md:text-xs uppercase text-white/70 tracking-[0.4em] mb-6"
            data-testid="landing-hero-eyebrow"
          >
            Crystal Media
          </motion.p>
          <motion.h1
            {...heroItem(reduce, 0.3)}
            className="font-presti text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[0.02em] leading-[1.05] text-white mb-10 lowercase"
            style={{ textShadow: '0 0 40px rgba(0,0,0,0.65)' }}
            data-testid="landing-hero-heading"
          >
            wedding photo + film
          </motion.h1>

          {/* Two crystal CTAs — sharp corners, subtle glass */}
          <motion.div {...heroItem(reduce, 0.5)} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
            <Magnetic>
              <Link
                to="/photo"
                className="crystal-btn crystal-btn-solid w-full sm:w-auto"
                data-testid="landing-photo-btn"
              >
                <span>Gallery</span>
              </Link>
            </Magnetic>
            <Magnetic>
              <Link
                to="/film/theatre"
                className="crystal-btn w-full sm:w-auto"
                data-testid="landing-film-btn"
              >
                <Play size={14} strokeWidth={1.75} />
                <span>Theatre</span>
              </Link>
            </Magnetic>
          </motion.div>

          <motion.div {...heroItem(reduce, 0.65)}>
            <Link
              to="/inquire"
              className="crystal-link"
              data-testid="landing-inquire-btn"
            >
              <span>Inquire Now</span>
              <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
          aria-hidden="true"
        >
          <span className="text-[9px] tracking-[0.35em] uppercase">Scroll</span>
          <span className="block w-px h-10 bg-white/30 overflow-hidden relative">
            <motion.span
              className="absolute inset-x-0 top-0 h-1/2 bg-white"
              animate={reduce ? {} : { y: ['-100%', '200%'] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </span>
        </motion.div>
      </section>

      {/* ─── FEATURED FILM ─── */}
      {featuredFilm && (
        <section className="bg-black text-white py-20 md:py-28 px-6 md:px-12" data-testid="landing-featured-film">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <Reveal>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Featured Film</p>
              <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.02em] leading-[1.1] mb-4">
                {featuredFilm.title}
              </h2>
              {featuredFilm.subtitle && (
                <p className="text-white/50 text-sm md:text-base mb-8 leading-relaxed">
                  {featuredFilm.subtitle}
                </p>
              )}
              <Magnetic>
                <Link
                  to="/film/theatre"
                  className="crystal-btn crystal-btn-solid"
                  data-testid="landing-enter-theatre-btn"
                >
                  <Play size={14} fill="black" />
                  <span>Enter Theatre</span>
                </Link>
              </Magnetic>
            </Reveal>

            <Reveal delay={0.15} y={40}>
            <Link
              to="/film/theatre"
              className="group relative aspect-video block overflow-hidden border border-white/15"
              data-testid="landing-featured-film-thumb"
            >
              {featuredFilm.thumbnail_url ? (
                <img
                  src={featuredFilm.thumbnail_url}
                  alt={featuredFilm.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <BackgroundVideo hlsUrl={featuredFilm.hls_url || ''} mp4Url={featuredFilm.video_url_low || featuredFilm.video_url} />
              )}
              <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors duration-500 flex items-center justify-center">
                <div
                  className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(18px)',
                    WebkitBackdropFilter: 'blur(18px)',
                    border: '1px solid rgba(255,255,255,0.4)'
                  }}
                >
                  <Play size={24} fill="white" className="ml-1 text-white" />
                </div>
              </div>
            </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── FEATURED COLLECTION (Gallery) ─── */}
      {featuredCollection && (
        <section className="bg-[#fafafa] py-20 md:py-28 px-6 md:px-12" data-testid="landing-featured-collection">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <Reveal y={40} className="order-2 md:order-1">
            <Link
              to={`/collection/${featuredCollection.id}`}
              className="group relative aspect-[4/5] block overflow-hidden border border-black/5"
              data-testid="landing-featured-collection-thumb"
            >
              <img
                src={featuredCollection.cover_image_url}
                alt={`${featuredCollection.couple} — ${featuredCollection.location || ''} wedding photography by Crystal Media`}
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                style={{
                  objectPosition: `${featuredCollection.cover_focal_point?.x || 50}% ${featuredCollection.cover_focal_point?.y || 50}%`
                }}
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </Link>
            </Reveal>

            <Reveal delay={0.15} className="order-1 md:order-2">
              <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">Featured Story</p>
              <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.02em] leading-[1.1] mb-2 text-black">
                {featuredCollection.couple}
              </h2>
              {featuredCollection.location && (
                <p className="text-black/50 text-sm md:text-base mb-8 leading-relaxed">
                  {featuredCollection.location}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Magnetic>
                  <Link
                    to="/photo"
                    className="lux-btn-dark"
                    data-testid="landing-enter-gallery-btn"
                  >
                    <span>Enter Gallery</span>
                  </Link>
                </Magnetic>
                <Link
                  to={`/collection/${featuredCollection.id}`}
                  className="lux-btn-ghost"
                  data-testid="landing-view-story-btn"
                >
                  <span>View This Story</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── TESTIMONIALS ─── */}
      {testimonials.length > 0 && (
        <section className="bg-white py-20 md:py-28 px-6 md:px-12" data-testid="landing-testimonials">
          <div className="max-w-6xl mx-auto">
            <Reveal className="text-center mb-12 md:mb-16">
              <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">Real Couples</p>
              <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.02em] text-black">
                What our couples say
              </h2>
            </Reveal>
            <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" stagger={0.14}>
              {testimonials.map((t, i) => (
                <StaggerItem as="figure" key={t.id || i} className="text-center md:text-left" data-testid={`landing-testimonial-${i}`}>
                  <blockquote className="font-presti italic text-lg md:text-xl text-black/80 leading-relaxed mb-5">
                    &ldquo;{t.text}&rdquo;
                  </blockquote>
                  <figcaption className="text-[11px] tracking-[0.25em] uppercase text-black/40">
                    — {t.name}
                    {t.role ? <span className="ml-1 normal-case tracking-normal">· {t.role}</span> : null}
                  </figcaption>
                </StaggerItem>
              ))}
            </Stagger>
            <Reveal delay={0.2} className="text-center mt-12">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-black/50 hover:text-black border-b border-black/15 hover:border-black pb-1 transition-colors"
                data-testid="landing-all-reviews-link"
              >
                Read all reviews <ArrowRight size={12} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* ─── FINAL CTA ─── */}
      <section className="bg-black text-white py-20 md:py-28 px-6 text-center relative overflow-hidden" data-testid="landing-final-cta">
        <Reveal className="max-w-2xl mx-auto relative z-10">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.02em] leading-[1.1] mb-5">
            Tell us about your wedding
          </h2>
          <p className="text-white/50 text-sm md:text-base mb-10 leading-relaxed">
            We take a limited number of weddings each year. Let&rsquo;s see if we&rsquo;re the right fit.
          </p>
          <Magnetic>
            <Link
              to="/inquire"
              className="crystal-btn crystal-btn-solid"
              data-testid="landing-final-inquire-btn"
            >
              <span>Inquire Now</span>
              <ArrowRight size={16} />
            </Link>
          </Magnetic>
        </Reveal>
      </section>

      {/* ─── FOOTER (minimal, no gold) ─── */}
      <footer className="bg-[#fafafa] py-10 px-6 border-t border-black/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-black/30">
            © {new Date().getFullYear()} Crystal Media · Toronto, Canada
          </p>
          <div className="flex items-center gap-6">
            <Link to="/photo" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Photo</Link>
            <Link to="/film" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Film</Link>
            {settings.inspo_visible !== false && (
              <Link to="/inspo" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Inspo</Link>
            )}
            <Link to="/partners" data-testid="footer-partners-link" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Partners</Link>
            <Link to="/vendors" data-testid="footer-vendors-link" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Vendors</Link>
            <Link to="/reviews" data-testid="footer-reviews-link" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Reviews</Link>
            <Link to="/about" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">About</Link>
            <Link to="/inquire" className="text-[10px] tracking-[0.2em] uppercase text-black/40 hover:text-black tap-target">Inquire</Link>
          </div>
        </div>
      </footer>

      {/* ─── FLOATING INQUIRE BUTTON (landing only) ─── */}
      <FloatingInquire visible={showFloatHint} />
    </div>
  );
};

/* Floating Inquire button → inquiry popup */
const FloatingInquire = ({ visible }) => {
  const [open, setOpen] = useState(false);
  if (!visible) return null;
  return (
    <>
      <InquiryPopup open={open} onClose={() => setOpen(false)} />
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open inquiry form"
          className="fixed z-[60] bottom-6 right-4 sm:right-6 flex items-center gap-2 px-5 py-3 text-white font-medium float-in tap-target"
          style={{
            background: 'rgba(0, 0, 0, 0.78)',
            backdropFilter: 'blur(20px) saturate(140%)',
            WebkitBackdropFilter: 'blur(20px) saturate(140%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
          data-testid="floating-inquire-trigger"
        >
          <span className="text-[11px] tracking-[0.25em] uppercase">Inquire</span>
        </button>
      )}
    </>
  );
};

export default LandingPage;
